import { Modal } from '@hue/design-system';
import { CircleHelp } from 'lucide-react';
import { useState } from 'react';

/// O passo a passo do autenticador. Mora num painel, e não na dica do ícone (`HelpTip`), porque não
/// cabe nela e porque as telas de ativação e de entrada existem antes da Ajuda do sistema: quem está
/// cadastrando o autenticador ainda não entrou (ADR 0081). A Ajuda repete o assunto no fluxo
/// "Código de segurança no celular ou no navegador".
const sections: readonly { title: string; items: readonly string[]; ordered?: boolean }[] = [
  {
    title: 'O que é',
    items: [
      'Além da senha, o sistema pede um código de 6 números, que muda a cada 30 segundos. Ele é criado por um aplicativo seu, assim ninguém entra só por ter descoberto a sua senha.',
    ],
  },
  {
    title: 'No celular',
    ordered: true,
    items: [
      'Instale um aplicativo autenticador, como o Google Authenticator ou o Microsoft Authenticator, pela App Store ou pela Play Store.',
      'Com o sistema aberto em outra tela, toque para adicionar uma conta no aplicativo e leia o QR Code.',
      'Com o sistema aberto no próprio celular, toque em Abrir no aplicativo. Se não abrir, use Copiar chave e cole no aplicativo, na opção de digitar uma chave.',
      'Digite no sistema os 6 números que o aplicativo mostrar.',
    ],
  },
  {
    title: 'No navegador do computador',
    ordered: true,
    items: [
      'Instale uma extensão de autenticador no navegador, como a Authenticator (authenticator.cc), pela loja de extensões do Chrome, Edge ou Firefox.',
      'Abra a extensão e use a opção de ler o QR Code da tela. Se preferir, use Copiar chave e cole na opção de adicionar manualmente.',
      'Digite no sistema os 6 números que a extensão mostrar.',
    ],
  },
  {
    title: 'Nos dois ao mesmo tempo',
    items: [
      'Antes de concluir, leia o mesmo QR Code (ou use a mesma chave) no celular e no navegador. Os dois passam a mostrar o mesmo código, e qualquer um deles serve para entrar.',
      'Ter nos dois evita ficar de fora se um deles faltar. Por outro lado, com o código no mesmo computador em que você digita a senha, esse computador passa a guardar as duas coisas: use só em um aparelho que seja seu.',
    ],
  },
  {
    title: 'Trocar do celular para o navegador, ou o contrário',
    ordered: true,
    items: [
      'Entre no sistema e abra a engrenagem de Configurações › Segurança.',
      'Clique em Trocar autenticador e informe sua senha e o código atual.',
      'Cadastre o QR Code novo no aparelho que você quer passar a usar e digite o código dele.',
      'Pronto: o cadastro antigo para de funcionar, e você já pode apagá-lo do outro aparelho. Até confirmar, ele continua valendo.',
    ],
  },
  {
    title: 'Perdi ou troquei de celular',
    items: [
      'Se ainda tiver o código em outro aparelho, entre com ele e faça a troca acima.',
      'Se não tiver, peça à pessoa responsável pelos acessos da empresa para usar Redefinir autenticador, em Configurações › Pessoas e acessos. Na sua próxima entrada, depois da senha, aparece um QR Code novo para cadastrar.',
    ],
  },
];

export function AuthenticatorHelp({ className = '' }: { className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        aria-label="Como usar o aplicativo autenticador"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        className={`grid size-10 shrink-0 place-items-center rounded-xl border border-surface-border bg-surface-card text-content-secondary transition hover:border-brand-500/60 hover:text-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${className}`}
      >
        <CircleHelp className="size-[18px]" aria-hidden="true" />
      </button>
      <Modal open={open} onOpenChange={setOpen} title="Como usar o aplicativo autenticador">
        <div className="space-y-5 text-sm leading-6">
          {sections.map((section) => {
            const List = section.ordered ? 'ol' : 'ul';
            return (
              <section key={section.title}>
                <h3 className="font-semibold">{section.title}</h3>
                <List
                  className={`mt-1.5 space-y-1.5 pl-5 text-content-secondary ${section.ordered ? 'list-decimal' : 'list-disc'}`}
                >
                  {section.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </List>
              </section>
            );
          })}
        </div>
      </Modal>
    </>
  );
}
